import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

/**
 * Generate a secure random token for portal access
 * @returns {string} A URL-safe random token (32 characters)
 */
export function generatePortalToken() {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  // Convert to base64url (URL-safe base64)
  return btoa(String.fromCharCode.apply(null, array))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '')
    .substring(0, 32);
}

/**
 * Generate a signing token (similar to existing logic)
 * @returns {string} A URL-safe random token
 */
export function generateSigningToken() {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return btoa(String.fromCharCode.apply(null, array))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '')
    .substring(0, 32);
}

/**
 * Ensure an estimate has a portalToken. If it doesn't exist, generate and save one.
 * @param {string} companyId - The company ID
 * @param {string} estimateId - The estimate ID
 * @returns {Promise<string>} The portal token
 */
export async function ensurePortalTokenForEstimate(companyId, estimateId) {
  try {
    const estimateRef = doc(db, 'companies', companyId, 'estimates', estimateId);
    const estimateDoc = await getDoc(estimateRef);
    
    if (!estimateDoc.exists()) {
      throw new Error('Estimate not found');
    }
    
    const estimateData = estimateDoc.data();
    
    // If portalToken already exists, return it
    if (estimateData.portalToken) {
      return estimateData.portalToken;
    }
    
    // Generate a new portalToken
    const portalToken = generatePortalToken();
    
    // Save it to the estimate
    await updateDoc(estimateRef, {
      portalToken: portalToken,
      updatedAt: new Date().toISOString()
    });
    
    return portalToken;
  } catch (error) {
    console.error('Error ensuring portal token:', error);
    throw error;
  }
}

/**
 * Ensure an estimate has a signingToken. If it doesn't exist, generate and save one.
 * @param {string} companyId - The company ID
 * @param {string} estimateId - The estimate ID
 * @returns {Promise<string>} The signing token
 */
export async function ensureSigningTokenForEstimate(companyId, estimateId) {
  try {
    const estimateRef = doc(db, 'companies', companyId, 'estimates', estimateId);
    const estimateDoc = await getDoc(estimateRef);
    
    if (!estimateDoc.exists()) {
      throw new Error('Estimate not found');
    }
    
    const estimateData = estimateDoc.data();
    
    // If signingToken already exists, return it
    if (estimateData.signingToken) {
      return estimateData.signingToken;
    }
    
    // Generate a new signingToken
    const signingToken = generateSigningToken();
    
    // Save it to the estimate
    await updateDoc(estimateRef, {
      signingToken: signingToken,
      updatedAt: new Date().toISOString()
    });
    
    return signingToken;
  } catch (error) {
    console.error('Error ensuring signing token:', error);
    throw error;
  }
}

/**
 * Build the portal URL for an estimate
 * @param {string} portalToken - The portal token
 * @param {string} baseUrl - Optional base URL (defaults to window.location.origin)
 * @returns {string} The full portal URL
 */
export function buildPortalUrl(portalToken, baseUrl = null) {
  const origin = baseUrl || (typeof window !== 'undefined' ? window.location.origin : '');
  return `${origin}/portal/${portalToken}`;
}

/**
 * Build the signing URL for an estimate
 * @param {string} estimateId - The estimate ID
 * @param {string} signingToken - The signing token
 * @param {string} baseUrl - Optional base URL (defaults to window.location.origin)
 * @returns {string} The full signing URL
 */
export function buildSigningUrl(estimateId, signingToken, baseUrl = null) {
  const origin = baseUrl || (typeof window !== 'undefined' ? window.location.origin : '');
  return `${origin}/sign-estimate/${estimateId}/${signingToken}`;
}

